/**
 * The bump chart (Design Review V-14; PRD 10.3; Phase 48): the capacity heatmap read as a
 * **ranking over time** — which roles are consistently at the top of the risk list, and which are
 * climbing.
 *
 * Every other rendering of this response draws a level: how hot a cell is, how tall a column is,
 * how far a dot sits from the line. A level answers "how bad is development in week 46". It does
 * not answer the question a planner asks in the third meeting about the same roster — *is this
 * the same role every week?* — and the two have different remedies. A role pinned at rank one all
 * quarter is a staffing problem: there are not enough of them and there will not be. A role that
 * was eighth in September and second by November is a pipeline problem: something landed. The
 * dashboard's "top risk services" list is one week of this chart, and one week cannot tell the
 * two apart.
 *
 * What a rank is
 * --------------
 * Within one week, the rows of the dimension ordered by the measure, worst first: rank 1 is the
 * row a planner should look at first. Ranks are **ordinal and distinct** — two rows cannot share
 * a y position on a line chart — so ties are broken deterministically and the model says which
 * ranks were ties, rather than pretending an order it does not have.
 *
 * Rows with no capacity that week
 * -------------------------------
 * A row with no capacity has no utilization: nothing to rank. It is not given rank last (that
 * would be a claim that it is fine) and not given rank one (that would be a claim that it is not).
 * It has **no rank that week**, the line breaks, and the card counts the gaps. That is the same
 * rule as the mosaic's "a row with no capacity has no column", restated for a ranking.
 *
 * Every number is a field of the `GET /forecast/heatmap` cells: this module orders and counts,
 * and computes no allocation of its own. Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { round2 } from './heatmap.js';

/** What the rows are ranked by. V-14 offers the first two; the third is what the hours say. */
export const BUMP_MEASURES = Object.freeze(['utilization', 'over_hours', 'allocated_hours']);

/** What each measure is called, and what rank 1 means under it. */
export const BUMP_MEASURE_LABELS = Object.freeze({
  utilization: 'Allocation',
  over_hours: 'Hours over',
  allocated_hours: 'Allocated hours',
});

/**
 * How many rows the chart draws before it starts leaving some out. Twelve lines is where a bump
 * chart stops being a ranking and starts being a plate of spaghetti; the rows left out are the
 * ones that never reached a good rank, they are counted, and the control moves the number.
 */
export const BUMP_ROWS_DEFAULT = 12;

/** How the drawn rows are chosen when there are more than fit. */
export const BUMP_PICKS = Object.freeze(['best_rank', 'last_rank', 'volatility']);

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** The value one cell is ranked on, or null where the cell cannot be ranked at all. */
export function cellValue(cell, measure = 'utilization') {
  if (!cell) return null;
  if (measure === 'over_hours') {
    // Hours over needs a capacity to be over: a cell without one has no answer, not a zero.
    if (!(Number(cell.capacity_hours) > 0)) return null;
    return round2(Math.max(0, Number(cell.allocated_hours) - Number(cell.capacity_hours)));
  }
  if (measure === 'allocated_hours') return round2(Number(cell.allocated_hours) || 0);
  return num(cell.utilization);
}

/**
 * One week's ranking: the rows that can be ranked, worst first, each with its ordinal rank.
 *
 * **Ties are broken deterministically**, which is the plan's own test. Equal values fall to the
 * row with more allocated hours (a hundred and ten per cent of ten people outranks a hundred and
 * ten per cent of one), and then to the row whose label sorts first — a rule that depends on
 * nothing but the data, so the same week ranks the same way in every run, in every browser, and
 * in the CSV. Each entry says whether its value tied with a neighbour, so the card can draw the
 * distinction rather than hide it.
 *
 * @param {{ key: string, label: string, cell: object }[]} entries
 * @param {string} measure
 */
export function rankWeek(entries, measure = 'utilization') {
  const ranked = entries
    .map((e) => ({
      key: e.key,
      label: e.label,
      value: cellValue(e.cell, measure),
      allocated_hours: round2(Number(e.cell?.allocated_hours) || 0),
      capacity_hours: round2(Number(e.cell?.capacity_hours) || 0),
      utilization: num(e.cell?.utilization),
      band: e.cell?.band ?? null,
      cell: e.cell,
    }))
    .filter((e) => e.value !== null)
    .sort(
      (a, b) =>
        b.value - a.value ||
        b.allocated_hours - a.allocated_hours ||
        String(a.label).localeCompare(String(b.label)) ||
        a.key.localeCompare(b.key),
    );
  return ranked.map((e, i) => ({
    ...e,
    rank: i + 1,
    tied:
      (i > 0 && ranked[i - 1].value === e.value) ||
      (i < ranked.length - 1 && ranked[i + 1].value === e.value),
  }));
}

/**
 * Which rows to keep when there are more than the chart can draw. Every one of them ends in the
 * label, so two rows that are equal on the measure are still ordered the same way every time —
 * the same discipline the tie rule inside a week follows.
 */
const pickComparator = (pick) => {
  const byName = (a, b) =>
    String(a.label).localeCompare(String(b.label)) || a.key.localeCompare(b.key);
  if (pick === 'last_rank')
    return (a, b) =>
      (a.last_rank ?? Infinity) - (b.last_rank ?? Infinity) ||
      (a.best_rank ?? Infinity) - (b.best_rank ?? Infinity) ||
      byName(a, b);
  if (pick === 'volatility') return (a, b) => b.swing - a.swing || byName(a, b);
  return (a, b) =>
    (a.best_rank ?? Infinity) - (b.best_rank ?? Infinity) ||
    (a.last_rank ?? Infinity) - (b.last_rank ?? Infinity) ||
    byName(a, b);
};

/**
 * Everything the chart draws, from a `GET /forecast/heatmap` response.
 *
 * @param {object} data  a heatmap response (any dimension; V-14 is about `service`)
 * @param {{ measure?: string, rows?: number, pick?: string, q?: string }} [opts]
 */
export function bumpModel(
  data,
  { measure = 'utilization', rows = BUMP_ROWS_DEFAULT, pick = 'best_rank', q = '' } = {},
) {
  const weeks = (data?.totals ?? []).map((t) => ({
    week_start: t.week_start,
    week_end: t.week_end,
  }));
  if (!weeks.length || !(data?.rows ?? []).length) return null;
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();
  const limit = Math.max(1, Math.trunc(rows) || BUMP_ROWS_DEFAULT);

  // One ranking per week, over every row of the response: a row's rank is its place among all of
  // them, never among the ones left on screen, so hiding a line cannot promote another.
  const byWeek = weeks.map((w, i) => ({
    ...w,
    index: i,
    ranks: rankWeek(
      (data.rows ?? []).map((row) => ({
        key: row.key,
        label: row.label,
        cell: row.cells?.[i] ?? null,
      })),
      measure,
    ),
  }));

  const rankOf = new Map();
  for (const week of byWeek)
    for (const entry of week.ranks) {
      if (!rankOf.has(entry.key)) rankOf.set(entry.key, new Map());
      rankOf.get(entry.key).set(week.week_start, entry);
    }

  const series = (data.rows ?? []).map((row) => {
    const own = rankOf.get(row.key) ?? new Map();
    const points = byWeek.map((w) => {
      const entry = own.get(w.week_start) ?? null;
      return {
        week_start: w.week_start,
        week_end: w.week_end,
        index: w.index,
        rank: entry?.rank ?? null,
        value: entry?.value ?? null,
        tied: entry?.tied ?? false,
        allocated_hours:
          entry?.allocated_hours ?? round2(Number(row.cells?.[w.index]?.allocated_hours) || 0),
        capacity_hours:
          entry?.capacity_hours ?? round2(Number(row.cells?.[w.index]?.capacity_hours) || 0),
        band: entry?.band ?? row.cells?.[w.index]?.band ?? null,
      };
    });
    const ranked = points.filter((p) => p.rank !== null);
    const first = ranked[0]?.rank ?? null;
    const last = ranked.at(-1)?.rank ?? null;
    const best = ranked.length ? Math.min(...ranked.map((p) => p.rank)) : null;
    const worst = ranked.length ? Math.max(...ranked.map((p) => p.rank)) : null;
    return {
      key: row.key,
      label: row.label,
      entity: row.entity ?? null,
      points,
      ranked_weeks: ranked.length,
      gaps: points.length - ranked.length,
      best_rank: best,
      worst_rank: worst,
      first_rank: first,
      last_rank: last,
      // Positive means it climbed the risk list: rank 8 to rank 2 is a move of six.
      move: first !== null && last !== null ? first - last : null,
      swing: best !== null && worst !== null ? worst - best : 0,
      weeks_at_one: ranked.filter((p) => p.rank === 1).length,
      // A row is "pinned" when it never left the top three: the staffing problem, not the
      // pipeline one, and the distinction the whole view exists to draw.
      pinned: ranked.length > 0 && ranked.every((p) => p.rank <= 3),
    };
  });

  const matched = needle
    ? series.filter((s) => String(s.label).toLowerCase().includes(needle))
    : series;
  const rankable = matched.filter((s) => s.ranked_weeks > 0);
  const drawn = [...rankable].sort(pickComparator(pick)).slice(0, limit);
  const drawnKeys = new Set(drawn.map((s) => s.key));

  return {
    dimension: data.dimension ?? 'service',
    measure,
    pick,
    rows: limit,
    from: weeks[0].week_start,
    to: weeks.at(-1).week_end,
    weeks: byWeek,
    weeks_count: weeks.length,
    series: drawn.sort((a, b) => (a.best_rank ?? Infinity) - (b.best_rank ?? Infinity)),
    all: series,
    hidden: rankable.length - drawn.length,
    hidden_keys: rankable.filter((s) => !drawnKeys.has(s.key)).map((s) => s.key),
    // Rows that could never be ranked in any week: no capacity anywhere, so no place in a list.
    unrankable: matched.filter((s) => s.ranked_weeks === 0),
    thresholds: data.thresholds ?? null,
    // The deepest ranking any week reached: the chart's own y axis.
    depth: Math.max(1, ...byWeek.map((w) => w.ranks.length)),
  };
}

/** The figures the card's header and notes state. */
export function bumpSummary(model) {
  const series = model?.series ?? [];
  const pinned = series.filter((s) => s.pinned);
  const climbers = [...series].filter((s) => (s.move ?? 0) > 0).sort((a, b) => b.move - a.move);
  const fallers = [...series].filter((s) => (s.move ?? 0) < 0).sort((a, b) => a.move - b.move);
  const leader = model?.weeks?.at(-1)?.ranks?.[0] ?? null;
  return {
    rows: series.length,
    hidden: model?.hidden ?? 0,
    unrankable: model?.unrankable?.length ?? 0,
    gaps: series.reduce((s, r) => s + r.gaps, 0),
    pinned,
    climber: climbers[0] ?? null,
    faller: fallers[0] ?? null,
    leader,
    // How often the top of the list changed hands: a ranking that never moves is a roster
    // problem, one that moves every week is a pipeline problem.
    lead_changes: (model?.weeks ?? []).reduce((count, w, i, arr) => {
      if (i === 0) return 0;
      const before = arr[i - 1].ranks[0]?.key;
      const now = w.ranks[0]?.key;
      return before && now && before !== now ? count + 1 : count;
    }, 0),
    ties: (model?.weeks ?? []).reduce((n, w) => n + w.ranks.filter((r) => r.tied).length, 0),
  };
}

// ---------------------------------------------------------------------------------------------
// Geometry

const r = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Where every point and line goes: weeks across, rank down, rank 1 at the top.
 *
 * A gap — a week a row could not be ranked in — **breaks the line**. Joining across it would draw
 * a movement that did not happen; leaving the row out entirely would lose the weeks it did have.
 * So a series becomes one or more segments, and the card can mark where they stop.
 *
 * @param {ReturnType<typeof bumpModel>} model
 * @param {{ width?: number, rowHeight?: number, top?: number, left?: number }} [opts]
 */
export function bumpLayout(model, { width = 640, rowHeight = 26, top = 0, left = 0 } = {}) {
  const weeks = model?.weeks ?? [];
  const series = model?.series ?? [];
  if (!weeks.length || !series.length) return { lines: [], columns: [], width, height: 0 };

  const step = weeks.length > 1 ? (width - left) / (weeks.length - 1) : 0;
  const x = (index) => r(left + index * step);
  const y = (rank) => r(top + (rank - 1) * rowHeight + rowHeight / 2);

  const columns = weeks.map((w) => ({
    week_start: w.week_start,
    week_end: w.week_end,
    index: w.index,
    x: x(w.index),
    ranks: w.ranks,
  }));

  const lines = series.map((row) => {
    const points = row.points
      .filter((p) => p.rank !== null)
      .map((p) => ({ ...p, x: x(p.index), y: y(p.rank) }));
    // Split on gaps: consecutive indexes only.
    const segments = [];
    let current = [];
    for (const point of points) {
      if (current.length && point.index !== current.at(-1).index + 1) {
        segments.push(current);
        current = [];
      }
      current.push(point);
    }
    if (current.length) segments.push(current);
    return {
      key: row.key,
      row,
      points,
      segments,
      paths: segments.map((seg) => seg.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ')),
      start: points[0] ?? null,
      end: points.at(-1) ?? null,
    };
  });

  return {
    lines,
    columns,
    width,
    height: top + (model.depth ?? series.length) * rowHeight,
    step,
    rowHeight,
    x,
    y,
  };
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)

/** One row per series: where it started, where it ended, and how far it moved. */
export function bumpColumns({ weeks = [] } = {}) {
  return [
    col.text('row', 'Row'),
    col.count('best_rank', 'Best rank'),
    col.count('worst_rank', 'Worst rank'),
    col.count('first_rank', 'First week'),
    col.count('last_rank', 'Last week'),
    col.number('move', 'Move'),
    col.count('weeks_at_one', 'Weeks at 1'),
    col.count('ranked_weeks', 'Weeks ranked'),
    col.count('gaps', 'Weeks unrankable'),
    ...weeks.map((w) => col.count(`week_${w.week_start}`, w.week_start)),
  ];
}

/** One row per series, with a column per week: the ranking as a grid, which is what exports. */
export function bumpExportRows(model) {
  return (model?.all ?? []).map((row) => ({
    row: row.label,
    best_rank: row.best_rank,
    worst_rank: row.worst_rank,
    first_rank: row.first_rank,
    last_rank: row.last_rank,
    move: row.move,
    weeks_at_one: row.weeks_at_one,
    ranked_weeks: row.ranked_weeks,
    gaps: row.gaps,
    ...Object.fromEntries(row.points.map((p) => [`week_${p.week_start}`, p.rank])),
  }));
}
